import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ShieldCheck, Compass, Target, Users } from 'lucide-react';
import { IMAGES, PRINCIPLES } from '../mock/mock';
import usePageMeta from '../hooks/usePageMeta';

const Section = ({ children, className = '' }) => (
  <section className={`max-w-[1400px] mx-auto px-6 lg:px-10 ${className}`}>{children}</section>
);

export default function About() {
  usePageMeta({
    title: 'About — Independent, Practical, Grounded in Execution',
    description: 'Chicago-based fractional technology leadership serving clients on six continents. Grounded in collaboration, trust, integrity, and execution.',
    path: '/about',
  });
  return (
    <div>
      <section className="bg-[#F5F7FA]">
        <Section className="pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-6">About Waypoint</div>
              <h1 className="font-serif text-5xl lg:text-7xl leading-[1.02] tracking-[-0.02em] text-[#0F2A44] text-balance">
                Independent, practical, grounded in <span className="italic text-[#3E6A94]">execution.</span>
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-slate-600 leading-relaxed">Based in Chicago. Working with clients on six continents. Trusted for what we build after the deck is closed.</p>
            </div>
          </div>
        </Section>
      </section>

      <Section className="py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-6">
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm">
              <img src={IMAGES.officeCity} alt="Chicago" className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-4">Who we are</div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-[1.08] text-[#0F2A44] tracking-tight">Seasoned executives with hands-on experience.</h2>
            <div className="mt-6 space-y-5 text-slate-600 leading-relaxed text-[17px]">
              <p>Waypoint’s fractional leaders have led programs at the scale where the details matter — where budgets are real, bandwidth is finite, and results are expected while the future is still being built.</p>
              <p>Our guidance is independent, practical, and grounded in execution. We help organizations build stable, confident technology, security, and privacy functions that support growth, reduce risk, and operate with discipline.</p>
              <p>Our brand rests on four commitments: <span className="text-[#0F2A44] font-medium">Collaboration</span> — the multiplier that turns capable teams into extraordinary ones. <span className="text-[#0F2A44] font-medium">Trust</span> — the currency of our relationships. <span className="text-[#0F2A44] font-medium">Execution</span> — because leadership without delivery is just an opinion. <span className="text-[#0F2A44] font-medium">Integrity</span> — in every decision, every recommendation, every partner conversation.</p>
            </div>
          </div>
        </div>
      </Section>

      {/* Pillars */}
      <section className="bg-[#0F2A44] text-white">
        <Section className="py-24">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#7DB4D8] mb-4">Brand pillars</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {[
              { icon: Users, title: 'Collaboration', body: 'The best outcomes come from teams that share context, share credit, and pull in the same direction.' },
              { icon: ShieldCheck, title: 'Trust', body: 'Long-term relationships built on candor, competence, and consistent follow-through.' },
              { icon: Target, title: 'Execution', body: 'Strategy is table stakes. Delivery is where reputations are earned.' },
              { icon: Compass, title: 'Integrity', body: 'Independent advice. No hidden incentives. The recommendation you’d want if you were in the room.' },
            ].map((v) => (
              <div key={v.title} className="bg-[#0F2A44] p-10">
                <div className="w-11 h-11 rounded-sm bg-white/10 flex items-center justify-center mb-6"><v.icon className="w-5 h-5 text-[#7DB4D8]" /></div>
                <div className="font-serif text-3xl mb-3">{v.title}</div>
                <p className="text-slate-300 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* Principles */}
      <Section className="py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-4">Operating model</div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05] text-[#0F2A44] tracking-tight">How we work.</h2>
            <p className="mt-6 text-slate-600 leading-relaxed max-w-md">Every engagement is anchored to five principles that keep transformation programs disciplined and outcomes measurable.</p>
          </div>
          <div className="lg:col-span-7 divide-y divide-slate-200 border-y border-slate-200">
            {PRINCIPLES.map((p, i) => (
              <div key={p.name} className="py-6 grid grid-cols-12 gap-4">
                <div className="col-span-1 text-slate-400 text-sm pt-1">{String(i + 1).padStart(2, '0')}</div>
                <div className="col-span-11 sm:col-span-4 font-serif text-xl text-[#0F2A44]">{p.name}</div>
                <div className="col-span-12 sm:col-span-7 text-slate-600 text-[15px] leading-relaxed">{p.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="bg-[#F5F7FA] border-t border-slate-200">
        <Section className="py-24 text-center">
          <h2 className="font-serif text-4xl lg:text-5xl text-[#0F2A44] tracking-tight max-w-3xl mx-auto text-balance">Let’s build something disciplined together.</h2>
          <div className="mt-4 text-sm text-slate-500">
            Reach the founder directly at{' '}
            <a href="mailto:info@waypointtransformation.com" className="text-[#0F2A44] underline underline-offset-4 decoration-slate-300 hover:decoration-[#0F2A44] transition-colors">
              info@waypointtransformation.com
            </a>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 mt-10 px-8 py-4 bg-[#0F2A44] text-white text-sm font-medium hover:bg-[#1E3A5F] transition-colors">
            Start a conversation <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Section>
      </section>
    </div>
  );
}
